/**
 * Supabase Browser Client — Vite edition
 *
 * Uses @supabase/supabase-js with localStorage session (no SSR cookie needed).
 * Singleton pattern — one client per browser tab.
 *
 * Auth-ready gate: all queries wait for session recovery before executing,
 * preventing RLS failures on first page load. (#710)
 *
 * @see Issue #88, #710
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { createLogger } from '@/lib/logger';

const log = createLogger('Supabase');
let _client: SupabaseClient | null = null;

/**
 * Auth-ready gate — resolves once the Supabase client has finished
 * recovering the session from localStorage. Without this, queries that
 * fire before session recovery complete send a bare anon-key JWT,
 * which RLS rejects → "first load fails, refresh works" symptom.
 */
let _authReadyResolve: (() => void) | null = null;
let _authReady = false;
const _authReadyPromise = new Promise<void>((resolve) => {
  _authReadyResolve = resolve;
});

/** Called by AuthProvider after initialize() completes */
export function markAuthReady(): void {
  if (!_authReady) {
    _authReady = true;
    _authReadyResolve?.();
    log.info('Auth ready');
  }
}

/** Wait for auth initialization (max 5s timeout to avoid deadlock) */
export function waitForAuth(): Promise<void> {
  if (_authReady) return Promise.resolve();
  return Promise.race([
    _authReadyPromise,
    new Promise<void>((resolve) => setTimeout(() => {
      // If still not ready after 5s, mark ready to unblock (degrade gracefully)
      if (!_authReady) {
        log.warn('Auth gate timeout — proceeding without session');
        markAuthReady();
      }
      resolve();
    }, 5000)),
  ]);
}

export function getSupabase(): SupabaseClient {
  if (_client) return _client;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    log.error('Missing env vars', { hasUrl: !!url, hasKey: !!key });
    throw new Error(
      "[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in environment"
    );
  }

  _client = createClient(url, key, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true, // for OAuth callback
    },
  });

  // #710 hardened: Mark auth ready on the FIRST auth state event from Supabase
  // This fires when the client finishes restoring the session from localStorage,
  // providing a second signal path independent of AuthProvider's initialize().
  _client.auth.onAuthStateChange(() => {
    if (!_authReady) markAuthReady();
  });

  // #574: Don't log full URL — redact to project ID only
  log.info('Client created', { project: url?.split('.')[0]?.split('//')[1] ?? 'unknown' });
  return _client;
}